import { useMemo } from 'react';
import { Box, Divider, SxProps } from '@mui/material';
import { PieChart, useItemTooltip } from '@mui/x-charts';

import useChartSettings from 'src/hooks/useChartSettings';
import { ChartPlaceholder } from 'src/components/chart/NoDataPlaceholder';
import { TooltipContainer, TooltipRow } from 'src/components/chart/ChartTooltip';
import SkeletonNoData from 'src/components/skeleton/SkeletonNoData';
import { readableStringFormatter } from 'src/utils/stringFormatter';
import { formatNumber } from 'src/utils/numberFormatter';
import { reorderArray } from 'src/utils/arrayFormatter';

//
//

const CustomItemTooltipContent = (props: any) => {
  const tooltipData = useItemTooltip();
  if (!tooltipData) return null;

  const currentItem = props[tooltipData.identifier.dataIndex as number];

  return (
    <TooltipContainer>
      <TooltipRow label={currentItem?.tooltipLabel} currentItem={{ color: tooltipData?.color }} showDot />
      <Divider sx={{ my: 1 }} />
      <TooltipRow
        label="Share:"
        value={`${currentItem?.percentageOfTransactions}%`}
        currentItem={currentItem}
      />
      <TooltipRow
        label="Passengers:"
        value={currentItem?.numberOfPassengers}
        currentItem={currentItem}
      />
      <TooltipRow
        label="Offset CO₂:"
        value={`${formatNumber(currentItem?.co2AmountKgs?.value)} ${currentItem?.co2AmountKgs?.unit?.toLowerCase()}`}
        currentItem={currentItem}
      />
    </TooltipContainer>
  );
};

interface Props {
  data?: any[];
  isLoading: boolean;
  height?: number;
  sx?: SxProps;
}

const DistanceBreakdown: React.FC<Props> = ({ data = [], isLoading, sx }) => {
  const noDataText = 'See a breakdown of carbon offsets by flight distance.';

  const distanceDefinitions: { [key: string]: number[] } = useMemo(() => {
    return {
      SHORT: [0, 2000],
      MEDIUM: [2001, 4000],
      LONG: [4001, 10000],
      ULTRA_LONG: [10000],
    };
  }, []);

  const distanceBreakdown = useMemo(() => {
    const column_order = Object.keys(distanceDefinitions);

    if (data?.length > 0) {
      const result = data?.map((item) => {
        const definition = distanceDefinitions[item?.distanceCategory];
        const label = definition?.length === 2
          ? `${definition[0].toLocaleString()} - ${definition[1].toLocaleString()} km`
          : `Over ${definition[0].toLocaleString()} km`;
        const distanceCategory = readableStringFormatter(item?.distanceCategory);

        return {
          ...item,
          label: distanceCategory,
          tooltipLabel: `${distanceCategory}: ${label}`,
          value: item?.numberOfTransactions,
          labelMarkType: 'square'
        };
      });

      const sortedArray = reorderArray(column_order, result, 'distanceCategory');
      return sortedArray;
    } else {
      return [];
    }
  }, [data, distanceDefinitions]);

  const { pieChartSetting } = useChartSettings(distanceBreakdown);

  return (
      <SkeletonNoData
        isLoading={isLoading}
        hasData={distanceBreakdown?.length > 0}
        text={noDataText}
        Placeholder={ChartPlaceholder}
        height={260}
      >
        <Box sx={{ width: '100%', height: '100%', ...sx }}>
          <PieChart
            series={[{ data: distanceBreakdown, type: 'pie' }]}
            slots={{
              tooltip: () => CustomItemTooltipContent(distanceBreakdown),
            }}
            {...pieChartSetting}
            height={pieChartSetting?.height}
          />
        </Box>
      </SkeletonNoData>
  );
};

export default DistanceBreakdown;
